import { useMemo } from 'react';
import {
  ClinicPetParent,
  ClinicEntityPhoneNumber,
  ClinicEntityCreationSource,
  useGetPetParentPhoneNumbersQuery,
} from 'shared/types/graphql';
import { getClinicPetParentDisplayName } from 'shared/utils';

type PetParent = Pick<ClinicPetParent, 'id' | 'firstName' | 'lastName' | 'creationSource' | 'isTest'>;

export interface PetParentWithDisplayName extends PetParent {
  displayName: string;
  isInboundSms: boolean;
  isTest: boolean;
}

export const usePetParentsWithDisplayName = <T extends PetParent>({
  petParents,
}: {
  petParents: T[];
}): { petParentsWithDisplayName: (T & PetParentWithDisplayName)[] } => {
  const petParentsWithPhoneStatus = petParents.map((petParent) => {
    const { creationSource, firstName, lastName, isTest } = petParent;
    return {
      ...petParent,
      isInboundSms:
        !!creationSource && creationSource === ClinicEntityCreationSource.InboundSms && !(firstName || lastName),
      isTest: !!isTest,
    };
  });

  const petParentsNeedingPhoneNumber = petParentsWithPhoneStatus.filter(({ isInboundSms }) => isInboundSms);

  const { data: petParentPhoneNumbers } = useGetPetParentPhoneNumbersQuery({
    variables: { clinicPetParentIds: petParentsNeedingPhoneNumber.map(({ id }) => id) },
    skip: !petParentsNeedingPhoneNumber.length,
  });

  const phoneNumbersByPetParentId = useMemo(
    () =>
      petParentPhoneNumbers?.findManyClinicPetParent.reduce((prev, curr) => {
        prev[curr.id] = curr.phoneNumbers;
        return prev;
      }, {} as Record<string, Pick<ClinicEntityPhoneNumber, 'updatedAt' | 'number' | 'isPrimary' | 'isDeleted'>[]>),
    [petParentPhoneNumbers],
  );

  const petParentsWithDisplayName = petParentsWithPhoneStatus.map((petParent) => {
    const { id, firstName, lastName, creationSource, isInboundSms, isTest } = petParent;
    const phoneNumbers = phoneNumbersByPetParentId?.[id] || [];
    return {
      ...petParent,
      displayName: getClinicPetParentDisplayName({ firstName, lastName, creationSource, phoneNumbers }),
      isInboundSms,
      isTest,
    };
  });

  return { petParentsWithDisplayName };
};
